<script setup>
import EvaluationScore from "./EvaluationScore";

defineProps({
  interview: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const emit = defineEmits(["closed"]);

function formatDate(date_string) {
  if (!date_string) return "N/A";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(date_string).toLocaleDateString(undefined, options);
}

function formatScore(score) {
  if (score == null) return "N/A";
  return `${Math.round(score * 100)}%`;
}

function onClose() {
  emit("closed");
}
</script>

<template>
  <div id="interview">
    <div class="d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <evaluation-score :score="interview.score" />
        <div class="ml-16">
          <div><b>{{ interview.title }}</b></div>
          <div class="text-color-black-50">{{ formatDate(interview.statusAt) }}</div>
        </div>
      </div>

      <span class="material-icons-round text-color-black-50 md-18 close-touchpoint" @click="onClose">
        close
      </span>
    </div>

    <div v-if="interview.summary" class="mt-16">
      <span class="field">Summary</span>
      <div class="card-container mt-8">{{ interview.summary }}</div>
    </div>

    <div v-if="interview.rubric_scores?.length" class="mt-16">
      <span class="field">Rubric Scores</span>
      <div class="rubric-list mt-8">
        <div v-for="rubric in interview.rubric_scores" :key="rubric.id" class="card-container mb-8">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <span class="font-weight-500">{{ rubric.category }}</span>
            <span class="rubric-score">{{ formatScore(rubric.score) }}</span>
          </div>
          <div v-if="rubric.reasoning">{{ rubric.reasoning }}</div>
        </div>
      </div>
    </div>

    <div v-if="interview.topics?.length" class="mt-16">
      <span class="field">Topics Discussed</span>
      <div class="topics-list mt-8">
        <el-tag v-for="topic in interview.topics" :key="topic.id" size="small" class="mr-8 mb-8">
          {{ topic.name }} ({{ topic.count }})
        </el-tag>
      </div>
    </div>

    <div v-if="interview.red_flags?.length" class="mt-16">
      <span class="field red-flag-label">Red Flags</span>
      <ul class="item-list mt-8">
        <li v-for="(flag, index) in interview.red_flags" :key="index" class="red-flag-item">{{ flag }}</li>
      </ul>
    </div>

    <div v-if="interview.unresolved_items?.length" class="mt-16">
      <span class="field">Unresolved Items</span>
      <ul class="item-list mt-8">
        <li v-for="(item, index) in interview.unresolved_items" :key="index" class="text-color-black-50">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card-container {
  background-color: $black-5;
  padding: 12px;
  border-radius: $tf-global-border-radius;
  white-space: pre-wrap;
}

.field {
  color: $black-50;
  font-weight: 500;
}

.rubric-score {
  font-weight: 600;
  color: $blue-100;
}

.red-flag-label {
  color: $magenta-100;
}

.red-flag-item {
  color: $magenta-100;
}

.item-list {
  margin: 0;
  padding-left: 20px;
  line-height: 1.6;
}

.close-touchpoint {
  cursor: pointer;
}
</style>
